import React, { useEffect, useMemo, useState } from "react"
import { CalendarIcon } from "@heroicons/react/outline"
import { graphql, useStaticQuery, navigate } from "gatsby"

// Storyblok option fields may come as strings, numbers, arrays, or objects like { value } / { name }.
function normalizeStoryblokOptionList(value) {
	if (value == null || value === "") return []
	if (typeof value === "string") {
		const t = value.trim()
		return t ? [t] : []
	}
	if (typeof value === "number" && !Number.isNaN(value)) {
		return [String(value)]
	}
	if (Array.isArray(value)) {
		return value.flatMap(v => normalizeStoryblokOptionList(v))
	}
	if (typeof value === "object") {
		if (value.value != null && value.value !== "") {
			return normalizeStoryblokOptionList(value.value)
		}
		if (value.name != null && value.name !== "") {
			return normalizeStoryblokOptionList(value.name)
		}
	}
	return []
}

const CourseUpcomingDates = ({ blok }) => {
	const sectionTitle = blok?.sectionTitle || "Upcoming Dates"

	const interestGroupTokens = useMemo(
		() => normalizeStoryblokOptionList(blok?.interestGroupNames),
		[JSON.stringify(blok?.interestGroupNames ?? null)]
	)

	const [eventsList, setEventsList] = useState([])

	const staticData = useStaticQuery(graphql`
		{
			allEventItem(
				sort: { order: ASC, fields: from }
				filter: { viewInList: { ne: false } }
			) {
				nodes {
					eventId
					permalink
					eventType
					name
					isOnDemand
					from
					to
				}
			}
			allEventInterestGroups {
				nodes {
					eventId
					interestGroups
				}
			}
			allInterestGroup {
				nodes {
					interestGroupId
					name
				}
			}
		}
	`)

	const allEvents = staticData.allEventItem.nodes
	const allEventInterestGroups = staticData.allEventInterestGroups.nodes
	const allInterestGroups = staticData.allInterestGroup.nodes

	// Resolve CRM interestGroupId from Storyblok value (may be ID or display name)
	const findInterestGroupId = (groups, token) => {
		if (!token || typeof token !== "string") return null
		const trimmed = token.trim()
		if (!trimmed) return null

		// Prefer matching by id if Storyblok stores datasource ids.
		const byId = groups.find(g => String(g.interestGroupId) === trimmed)
		if (byId) return byId.interestGroupId

		// Fallback: match by display name.
		const lower = trimmed.toLowerCase()
		const byName = groups.find(
			g => (g.name || "").trim().toLowerCase() === lower
		)
		return byName?.interestGroupId ?? null
	}

	// Create eventId -> [interestGroupIds]
	const buildEventGroupMap = eventGroups => {
		const map = new Map()
		eventGroups.forEach(item => {
			if (item.interestGroups?.length) {
				map.set(item.eventId, item.interestGroups)
			}
		})
		return map
	}

	const formatDateRange = (from, to) => {
		if (!from) return ""
		const fromDate = new Date(from)
		const toDate = to ? new Date(to) : null

		const format = date =>
			new Intl.DateTimeFormat("en-AU", {
				timeZone: "Australia/NSW",
				day: "2-digit",
				month: "short",
				year: "numeric",
			}).format(date)

		return toDate ? `${format(fromDate)} - ${format(toDate)}` : format(fromDate)
	}

	const formatTimeRange = (from, to) => {
		if (!from || !to) return ""
		const format = date =>
			new Intl.DateTimeFormat("en-AU", {
				timeZone: "Australia/NSW",
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			}).format(new Date(date))

		return `${format(from)} - ${format(to)}`
	}

	// Get the correct URL for an event based on eventType
	const getEventUrl = event => {
		if (!event?.permalink) return "#"

		const eventTypeLower = (event.eventType || "").toLowerCase()
		const isTraining =
			eventTypeLower === "pd workshops" || eventTypeLower === "online learning"

		const baseRoute = isTraining ? "/training" : "/events"
		return `${baseRoute}/${event.permalink}`
	}

	useEffect(() => {
		const now = new Date()

		const hasInterestFilter = interestGroupTokens.length > 0

		// If nothing is selected in Storyblok, show empty list.
		if (!hasInterestFilter) {
			setEventsList([])
			return
		}

		// Filter by Interest Group (resolve selected tokens -> CRM ids)
		const resolvedInterestGroupIds = [
			...new Set(
				interestGroupTokens
					.map(t => findInterestGroupId(allInterestGroups, t))
					.filter(Boolean)
			),
		]
		const eventGroupMap = buildEventGroupMap(allEventInterestGroups)

		// Exclude events if we couldn't resolve the interest group id(s).
		if (!resolvedInterestGroupIds.length) {
			setEventsList([])
			return
		}

		const interestFiltered = allEvents.filter(event => {
			const matchDate = !event.isOnDemand && new Date(event.from) >= now
			if (!matchDate) return false

			const groupsForEvent = eventGroupMap.get(event.eventId) || []
			return resolvedInterestGroupIds.some(id =>
				groupsForEvent.some(g => String(g) === String(id))
			)
		})

		// Sort by earliest upcoming event
		interestFiltered.sort((a, b) => new Date(a.from) - new Date(b.from))

		setEventsList(interestFiltered)
	}, [interestGroupTokens, allEvents, allEventInterestGroups, allInterestGroups])

	const CourseRow = ({ event }) => {
		const dateRange = formatDateRange(event.from, event.to)
		const timeRange = formatTimeRange(event.from, event.to)

		return (
			<div className="flex items-center justify-between py-4 px-6 cursor-pointer hover:bg-gray-50 transition">
				<div className="flex items-center flex-1">
					<CalendarIcon className="h-5 w-5 text-gray-500 mr-4" />
					<div>
						<div className="font-semibold text-gray-900 text-base">
							{event.name}
						</div>

						<div className="text-sm text-gray-700">
							{dateRange}
							{timeRange && (
								<>
									{" "}
									<span className="text-gray-600">{timeRange}</span>{" "}
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="bg-gray-200 px-2 md:px-16 lg:px-28">
			<div className="pt-10 md:pt-16 pb-8 md:pb-24">
				<h2 className="text-xl md:text-2xl font-bold text-neutral mb-6 px-4">
					{sectionTitle}
				</h2>

				{eventsList.length > 0 ? (
					<div className="mx-2 bg-white rounded-lg shadow-sm border border-gray-200 divide-y">
						{eventsList.slice(0, 6).map(event => {
							const eventUrl = getEventUrl(event)
							return (
								<div key={event.eventId} onClick={() => navigate(eventUrl)}>
									<CourseRow event={event} />
								</div>
							)
						})}
					</div>
				) : (
					<div className="text-center py-12">
						<p className="text-gray-600 text-lg">
							{interestGroupTokens.length === 0
								? "Please select an Interest Group in Storyblok."
								: "No upcoming courses found."}
						</p>
					</div>
				)}
			</div>
		</div>
	)
}

export default CourseUpcomingDates
